import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider
} from '@mui/material';
import { Leaderboard as StandingsIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import unifiedApi from '../utils/unifiedApi';
import CumulativePointsReport from '../components/reports/CumulativePointsReport';
import LeadingScorers from '../components/statistics/LeadingScorers';
import GoldenGlove from '../components/statistics/GoldenGlove';
import SEOMetadata from '../components/SEOMetadata';
import StandingsComponent from '../components/standings/StandingComponent';
import PlayoffPoolStandings from '../components/standings/PlayoffPoolStandings';
import PlayoffBracketSection from '../components/standings/PlayoffBracketSection';

// Custom StandingsComponent wrapper that handles league info callback
const StandingsTable = ({ leagueSlug, onLeagueInfoLoaded }) => {
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStandings = async () => {
      try {
        setLoading(true);
        setError(null);

        const standingsData = await unifiedApi.getLeagueStandings(leagueSlug);
        
        if (!standingsData) {
          setError('League not found or not public');
          return;
        }

        setStandings(standingsData);
        // Notify parent component about the league info
        if (onLeagueInfoLoaded) {
          onLeagueInfoLoaded(standingsData);
        }
      } catch (err) {
        setError('Failed to load standings');
        console.error('Error loading standings:', err);
      } finally {
        setLoading(false);
      }
    };

    if (leagueSlug) {
      loadStandings();
    }
  }, [leagueSlug, onLeagueInfoLoaded]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <Typography>Loading standings...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box my={4}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!standings) {
    return null;
  }

  // Use the existing StandingsComponent but with public data
  return (
    <StandingsComponent 
      leagueId={standings.leagueId} 
      orgId={null}
      standingsData={standings}
      isPublic={true}
      hideTitle={true}
    />
  );
};

const Standings = () => {
  const { leagueSlug } = useParams();
  const [leagueInfo, setLeagueInfo] = useState(null);
  const [playoffConfig, setPlayoffConfig] = useState(null);
  const [playoffStandings, setPlayoffStandings] = useState(null);
  const [playoffBracket, setPlayoffBracket] = useState(null);

  const handleLeagueInfoLoaded = (standings) => {
    setLeagueInfo(standings);
  };

  useEffect(() => {
    const loadPlayoffData = async () => {
      try {
        const config = await unifiedApi.getPublicPlayoffConfig(leagueSlug);
        if (!config || !config.isActive) return;
        setPlayoffConfig(config);

        // Fetch standings and bracket in parallel
        const [standings, bracket] = await Promise.all([
          unifiedApi.getPlayoffStandings(leagueSlug),
          unifiedApi.getPlayoffBracket(leagueSlug),
        ]);
        if (standings) setPlayoffStandings(standings);
        if (bracket) setPlayoffBracket(bracket);
      } catch (err) {
        // Playoff data is optional — don't block the page on errors
        console.error('Error loading playoff data:', err);
      }
    };

    if (leagueSlug) {
      loadPlayoffData();
    }
  }, [leagueSlug]);

  return (
    <Box>
      {leagueInfo && (
        <SEOMetadata 
          title={`${leagueInfo.leagueName} - Standings`}
          description={`Current standings and team statistics for ${leagueInfo.leagueName}`}
          keywords={`${leagueInfo.leagueName}, standings, soccer, football, league table, teams`}
        />
      )}
      
      {/* Page Header */}
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <StandingsIcon color="primary" />
          {leagueInfo?.leagueName || 'League'} - Standings
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Current league standings and team statistics
        </Typography>
      </Box>

      {/* Use the existing StandingsComponent */}
      <StandingsTable leagueSlug={leagueSlug} onLeagueInfoLoaded={handleLeagueInfoLoaded} />

      {/* Playoff Standings */}
      {playoffConfig?.isActive && playoffStandings?.pools?.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Divider sx={{ mb: 3 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrophyIcon color="primary" />
            <Typography variant="h5" component="h2" fontWeight="bold">
              Playoffs
            </Typography>
          </Box>
          {playoffStandings.pools.map((pool) => (
            <PlayoffPoolStandings key={pool.poolId} pool={pool} scoring={playoffStandings.scoring} />
          ))}
        </Box>
      )}

      {/* Playoff Bracket */}
      {playoffConfig?.isActive && playoffBracket && (
        <PlayoffBracketSection bracket={playoffBracket} leagueSlug={leagueSlug} />
      )}

      {/* Cumulative Points Section */}
      {leagueInfo && (
        <Box sx={{ mt: 4 }}>
          <CumulativePointsReport leagueKey={leagueSlug} />
        </Box>
      )}

      {/* Leading Scorers Section */}
      {leagueInfo && (
        <Box sx={{ mt: 4 }}>
          <LeadingScorers 
            leagueSlug={leagueSlug}
            limit={10}
            minimumGoals={0}
            title="Top Goal Scorers"
            isPublic={true}
          />
        </Box>
      )}

      {/* Golden Glove Section */}
      {leagueInfo && (
        <Box sx={{ mt: 4 }}>
          <GoldenGlove 
            leagueSlug={leagueSlug}
            limit={5}
            minimumCleanSheets={0}
            title="Golden Glove"
            isPublic={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default Standings;